import {
    ApplicationRoleCode as ApplicationRoleCodeEnum,
    CurrentUserUpdateRequest,
    ICurrentUserUpdateRequest,
    IUserProfileResponse,
    IUserProfileUpdateRequest,
    IUserUpdateRequest,
    UserProfileUpdateRequest,
    UserUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';

export function profileResponseToUpdateRequest(
    profile: IUserProfileResponse
): IUserProfileUpdateRequest {
    return {
        ...profile,
        TimeZone: profile.TimeZoneCode,
        DateFormat: profile.DateFormatCode,
        TimeFormat: profile.TimeFormatCode,
    };
}

/**
 * Updates the user profile given the user's id and the updates to make. Loads the
 * profile to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param userId The id of the user to update
 * @param updates The fields to update on the user profile
 */
export async function updateUserProfile(
    userId: string,
    updates: Partial<IUserProfileUpdateRequest>
) {
    const profile = await sdClient.getUserProfile({ userId });

    const request = new UserProfileUpdateRequest({
        ...profileResponseToUpdateRequest(profile),
        ...updates,
    });

    return sdClient.updateUserProfile({ userId, body: request });
}

/**
 * Updates the user given the user's id and the updates to make. Loads the
 * user to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param userId The id of the user to update
 * @param updates The fields to update on the user
 */
export async function updateUser(userId: string, updates: Partial<IUserUpdateRequest>) {
    const { ApplicationRoleCode, ...rest } = await sdClient.getUser({ userId });

    const request = new UserUpdateRequest({
        ...rest,
        ApplicationRoleCode: (ApplicationRoleCode as unknown) as ApplicationRoleCodeEnum,
        ...updates,
    });

    return sdClient.updateUser({ userId, body: request });
}

/**
 * Updates the current user given the updates to make. Loads the
 * user to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param updates The fields to update on the current user
 */
export async function updateCurrentUser(updates: Partial<ICurrentUserUpdateRequest>) {
    const {
        AcceptedEula,
        FirstName,
        LastName,
        PhoneNumber,
    } = await sdClient.getCurrentUser({});
    const request = new CurrentUserUpdateRequest({
        AcceptEula: AcceptedEula,
        FirstName,
        LastName,
        PhoneNumber,
        ...updates,
    });
    return sdClient.updateCurrentUser({ body: request });
}
